'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Navbar, Footer } from '../../components/Layout';
import { BookOpen, Calendar, Clock, ArrowRight, PlayCircle, Loader2 } from 'lucide-react';
import ProductImage from '../../components/ProductImage';

const FALLBACK_BLOGS = [
  {
    id: 1,
    title: "The Magic of Wood Pressed Cold Oils",
    content: "Traditional cold pressing methods preserve natural nutrients and aromas of groundnuts and sesame seeds. Modern heat extraction methods destroy vital enzymes. Discover why cold pressed oils are the key to a healthy heart...",
    image: "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?q=80&w=2036&auto=format&fit=crop",
    type: "ARTICLE",
    videoUrl: "",
    createdAt: "2026-05-15T10:00:00"
  },
  {
    id: 2,
    title: "Traditional Millet Upma Recipe",
    content: "Learn how to make a delicious and healthy breakfast using Foxtail Millet (Thinai). This traditional Tamil recipe is high in fiber, low in glycemic index, and keeps you active throughout the day. Perfect for weight loss...",
    image: "https://images.unsplash.com/photo-1590779033100-9f60a05a013d?q=80&w=1974&auto=format&fit=crop",
    type: "RECIPE",
    videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    createdAt: "2026-05-20T08:30:00"
  },
  {
    id: 3,
    title: "Understanding A2 Kangayam Cow Ghee",
    content: "Kangayam is a native breed of Tamil Nadu. The ghee prepared from Kangayam cow milk contains A2 beta-casein protein which is highly digestible and packed with fat-soluble vitamins. Here is how it is prepared traditionally...",
    image: "https://images.unsplash.com/photo-1589301760014-d929f3979dbc?q=80&w=2070&auto=format&fit=crop",
    type: "ARTICLE",
    videoUrl: "",
    createdAt: "2026-05-25T14:15:00"
  }
];

export default function BlogsPage() {
  const [blogs, setBlogs] = useState<any[]>([]);
  const [filter, setFilter] = useState<'ALL' | 'ARTICLE' | 'RECIPE'>('ALL');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const productApi = process.env.NEXT_PUBLIC_PRODUCT_API || 'http://localhost:8081';
    
    fetch(`${productApi}/api/blogs`)
      .then(res => res.json())
      .then(data => {
        let list = [];
        if (data && data.data) {
          list = data.data;
        } else if (Array.isArray(data)) {
          list = data;
        }
        
        if (list.length > 0) {
          setBlogs(list);
        } else {
          setBlogs(FALLBACK_BLOGS);
        }
      })
      .catch(err => {
        console.warn('Failed to load blogs, using fallbacks', err);
        setBlogs(FALLBACK_BLOGS);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const filteredBlogs = blogs.filter(b => filter === 'ALL' || b.type === filter);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAF5] flex flex-col">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center py-24">
          <Loader2 className="w-12 h-12 text-mid-green animate-spin mb-4" />
          <span className="text-text-muted font-bold text-sm">Loading articles & recipes...</span>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9F5] flex flex-col font-body selection:bg-amber/30 selection:text-deep-green">
      <Navbar />

      <main className="flex-1 max-w-[1400px] w-full mx-auto px-4 sm:px-6 md:px-12 py-6 sm:py-12">
        {/* Simple Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-amber font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-1.5 mb-3">
            <BookOpen className="w-4 h-4" /> Organic Wisdom
          </span>
          <h1 className="text-3xl md:text-5xl font-display font-black text-deep-green leading-tight">
            Our Blogs & Recipes
          </h1>
          <div className="h-[2px] w-24 bg-gradient-to-r from-amber via-mid-green to-transparent rounded-full mx-auto mt-4 mb-6" />
          <p className="text-text-muted font-medium text-sm md:text-base leading-relaxed">
            Read articles on sustainable farming, health benefits of native products, and recipes prepared using organic ingredients.
          </p>
        </div>

        {/* Filter controls */}
        <div className="flex justify-center gap-3 mb-10">
          {(['ALL', 'ARTICLE', 'RECIPE'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all border ${
                filter === f
                  ? 'bg-deep-green text-white border-deep-green shadow-md shadow-deep-green/10'
                  : 'bg-white text-deep-green border-border/80 hover:bg-pale-green/40'
              }`}
            >
              {f === 'ALL' ? 'All Feeds' : f === 'ARTICLE' ? 'Articles' : 'Recipes'}
            </button>
          ))}
        </div>

        {/* Blog Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8 text-left">
          {filteredBlogs.map(blog => {
            const dateStr = new Date(blog.createdAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              timeZone: 'UTC'
            });
            return (
              <Link 
                key={blog.id} 
                href={`/blog/${blog.id}`}
                className="bg-white rounded-[2rem] border border-border/50 overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 flex flex-col group block cursor-pointer"
              >
                {/* Image Wrap */}
                <div className="h-56 bg-surface relative overflow-hidden shrink-0 border-b border-border/20">
                  {blog.image ? (
                    <ProductImage 
                      src={blog.image} 
                      alt={blog.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-text-muted font-semibold">No Image Preview</div>
                  )}
                  
                  {/* Floating Tag */}
                  <span className={`absolute top-4 left-4 text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full shadow-md ${
                    blog.type === 'RECIPE' 
                      ? 'bg-amber text-deep-green' 
                      : 'bg-deep-green text-white'
                  }`}>
                    {blog.type}
                  </span>
 
                  {blog.videoUrl && (
                    <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-sm text-white px-2.5 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1">
                      <PlayCircle className="w-3.5 h-3.5 text-amber" /> Video Included
                    </div>
                  )}
                </div>
 
                {/* Content */}
                <div className="p-6 flex-1 flex flex-col justify-between">
                  <div className="space-y-3">
                    <div className="flex items-center gap-4 text-[10px] text-text-muted font-bold uppercase tracking-wider">
                      <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-amber" /> {dateStr}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-amber" /> 5 Min Read</span>
                    </div>
 
                    <h3 className="font-display font-black text-xl text-deep-green group-hover:text-mid-green transition-colors leading-tight line-clamp-2">
                      {blog.title}
                    </h3>
 
                    <p className="text-text-muted text-xs font-medium leading-relaxed line-clamp-3">
                      {blog.content}
                    </p>
                  </div>
 
                  <div className="pt-6 mt-6 border-t border-border/40 flex justify-between items-center shrink-0">
                    <div 
                      className="text-xs font-black uppercase text-mid-green group-hover:text-deep-green transition-colors flex items-center gap-1 tracking-wider"
                    >
                      Read full story <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}

          {filteredBlogs.length === 0 && (
            <div className="col-span-full py-16 text-center text-text-muted font-bold text-sm bg-white border border-dashed border-border rounded-[2rem] p-8">
              No blog posts match the selected type filter.
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
